// Utilidades de fecha para la app. Las fechas del backend llegan como ISO 8601
// con offset (p. ej. "2026-06-30T14:30:00-04:00"); new Date() las interpreta como
// el instante absoluto correcto, así que "cuánto falta" no depende del huso del navegador.

const pad = (n: number) => String(n).padStart(2, "0");

/** "DD-MM-YYYY HH:MM" en hora local (o "—"). */
export function formatFechaHora(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Solo fecha "DD-MM-YYYY" (o "—"). */
export function formatFecha(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
}

/**
 * Cuánto falta hasta la fecha, en granularidad gruesa: "2 meses", "20 días",
 * "4 horas", "15 min", "vencido". Devuelve null si no hay fecha.
 */
export function tiempoRestante(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const diff = target - Date.now();
  if (diff <= 0) return "vencido";

  const HORA = 3_600_000;
  const DIA = 24 * HORA;
  const dias = Math.floor(diff / DIA);
  if (dias >= 30) {
    const meses = Math.floor(dias / 30);
    return `${meses} ${meses === 1 ? "mes" : "meses"}`;
  }
  if (dias >= 1) return `${dias} ${dias === 1 ? "día" : "días"}`;
  const horas = Math.floor(diff / HORA);
  if (horas >= 1) return `${horas} ${horas === 1 ? "hora" : "horas"}`;
  const min = Math.max(1, Math.floor(diff / 60_000));
  return `${min} min`;
}

/**
 * Cuánto pasó desde la fecha, en granularidad gruesa: "hace 3 h", "hace 2 días".
 * Es el contrapunto de `tiempoRestante`, para fechas que ya ocurrieron.
 */
export function tiempoTranscurrido(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const desde = new Date(iso).getTime();
  if (Number.isNaN(desde)) return null;
  const diff = Date.now() - desde;
  if (diff < 60_000) return "recién";

  const HORA = 3_600_000;
  const DIA = 24 * HORA;
  const dias = Math.floor(diff / DIA);
  if (dias >= 30) {
    const meses = Math.floor(dias / 30);
    return `hace ${meses} ${meses === 1 ? "mes" : "meses"}`;
  }
  if (dias >= 1) return `hace ${dias} ${dias === 1 ? "día" : "días"}`;
  const horas = Math.floor(diff / HORA);
  if (horas >= 1) return `hace ${horas} h`;
  return `hace ${Math.floor(diff / 60_000)} min`;
}
